import React, { useState, useEffect } from "react";
import {
  message,
  Spin,
  Row,
  Col,
  Grid,
  Select,
  Space,
  Typography,
  Checkbox,
  Switch,
  Tooltip,
  Button,
} from "antd";
import {
  InfoCircleOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import Header from "./Header";
import FilterSidebar from "./FilterSidebar";
import MaxAvailChart from "./MaxAvailChart";
import DataTable from "./DataTable";
import { api } from "../../services/api";
import { transformMaxAvailData } from "../../utils/dataTransform";

const ALL_REGIONS = ["NSW1", "QLD1", "VIC1", "SA1", "TAS1"];

const MaxAvailPage = () => {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  // Load saved settings from localStorage or use defaults
  const loadSavedSettings = () => {
    try {
      const saved = localStorage.getItem("maxavail_user_settings");
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      // Ignore invalid saved settings
    }
    // Return default settings if nothing saved
    return {
      chartLevel: "region",
      selectedRegions: ["NSW1", "QLD1", "VIC1", "SA1", "TAS1"],
      selectedVariable: "generation_max_availability", // Default variable (single selection)
      showOnlyZeroAvail: true,
      viewMode: "chart",
      autoRefresh: true,
      sharedTooltip: false,
    };
  };

  const savedSettings = loadSavedSettings();

  // State with saved or default user settings
  const [runDateTime, setRunDateTime] = useState(null);
  const aggregationLevel = "duid"; // Fixed to DUID aggregation for now
  const [chartLevel, setChartLevel] = useState(savedSettings.chartLevel);
  const [selectedRegions, setSelectedRegions] = useState(
    savedSettings.selectedRegions
  );
  const [selectedFuelTypes, setSelectedFuelTypes] = useState(
    savedSettings.selectedFuelTypes || []
  );
  const [selectedVariable, setSelectedVariable] = useState(
    savedSettings.selectedVariable || "generation_max_availability"
  ); // Single chart variable
  const [selectedPortfolios, setSelectedPortfolios] = useState([]); // V2: New state
  const [selectedStations, setSelectedStations] = useState([]); // V2: New state
  const [selectedDuids, setSelectedDuids] = useState([]); // V2: New state
  const [showOnlyZeroAvail, setShowOnlyZeroAvail] = useState(
    savedSettings.showOnlyZeroAvail !== undefined
      ? savedSettings.showOnlyZeroAvail
      : true
  );
  const [viewMode, setViewMode] = useState(savedSettings.viewMode);
  const [autoRefresh, setAutoRefresh] = useState(savedSettings.autoRefresh);
  const [sharedTooltip, setSharedTooltip] = useState(
    savedSettings.sharedTooltip
  );

  const [maxAvailData, setMaxAvailData] = useState([]);
  const [maxAvailRawData, setMaxAvailRawData] = useState([]);

  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [filtersInitialized, setFiltersInitialized] = useState(false); // Track if filters have been set initially
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false); // Track sidebar collapse state
  const [sidebarCheckedKeys, setSidebarCheckedKeys] = useState(
    savedSettings.sidebarCheckedKeys || []
  );

  // V2: Available options for filtering
  const [availableFuelTypes, setAvailableFuelTypes] = useState([]);

  // Save user settings to localStorage whenever they change
  useEffect(() => {
    const settings = {
      chartLevel,
      selectedRegions,
      selectedFuelTypes,
      selectedVariable,
      showOnlyZeroAvail,
      viewMode,
      autoRefresh,
      sharedTooltip,
      sidebarCheckedKeys,
    };
    try {
      localStorage.setItem("maxavail_user_settings", JSON.stringify(settings));
    } catch (e) {
      // Ignore save errors
    }
  }, [
    chartLevel,
    selectedRegions,
    selectedFuelTypes,
    selectedVariable,
    showOnlyZeroAvail,
    viewMode,
    autoRefresh,
    sharedTooltip,
    sidebarCheckedKeys,
  ]);

  // Fetch initial run times
  useEffect(() => {
    const fetchRunTimes = async () => {
      try {
        const aemoResponse = await api.getMaxRunDateTime();
        setRunDateTime(aemoResponse.run_datetime);
      } catch (error) {
        message.error("Failed to fetch run times");
      }
    };

    fetchRunTimes();
  }, []);

  // Manual refresh trigger
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Handle sidebar selection changes - update chart immediately
  const handleSidebarSelectionChange = (selection) => {
    const { region_ids, portfolio_ids, station_ids, duids, checkedKeys } = selection;
    setSelectedRegions(region_ids);
    setSelectedPortfolios(portfolio_ids);
    setSelectedStations(station_ids);
    setSelectedDuids(duids);
    setSidebarCheckedKeys(checkedKeys);
  };

  // Check for latest data and re-fetch from the API
  const handleRefresh = async () => {
    setLoading(true);
    try {
      const response = await api.getMaxRunDateTime();
      if (response.run_datetime !== runDateTime) {
        setRunDateTime(response.run_datetime);
      } else {
        setRefreshTrigger((prev) => prev + 1);
      }
    } catch (error) {
      message.error("Failed to check for latest data");
      setLoading(false);
    }
  };

  // Effect 1: Fetch data when refresh is triggered or rundatetime changes
  useEffect(() => {
    if (!runDateTime) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const maxAvailResponse = await api.getMaxAvailData(
          runDateTime,
          aggregationLevel,
          ALL_REGIONS
        );

        setMaxAvailRawData(maxAvailResponse);

        // Extract available fuel types and portfolios for filtering
        if (maxAvailResponse && maxAvailResponse.length > 0) {
          const fuelTypes = [
            ...new Set(
              maxAvailResponse.map((row) => row.fuel_type).filter(Boolean)
            ),
          ];
          const portfolios = [
            ...new Set(
              maxAvailResponse.map((row) => row.portfolioid).filter(Boolean)
            ),
          ];

          setAvailableFuelTypes(fuelTypes);

          // Initialize fuel types (only on first load, if not restored from saved settings)
          if (!filtersInitialized) {
            if (selectedFuelTypes.length === 0) {
              const defaultFuelTypes = fuelTypes.filter(
                (ft) =>
                  ft &&
                  (ft.toLowerCase().includes("gas") ||
                    ft.toLowerCase().includes("coal"))
              );
              setSelectedFuelTypes(
                defaultFuelTypes.length > 0 ? defaultFuelTypes : fuelTypes
              );
            }
            setFiltersInitialized(true);
          }
        }

        if (initialLoad) {
          message.success("Data loaded successfully");
          setInitialLoad(false);
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
        message.error("Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [runDateTime, refreshTrigger]);

  // Effect 2: Filter and transform raw data when filter controls change
  useEffect(() => {
    if (!maxAvailRawData || maxAvailRawData.length === 0) return;

    let filteredData = maxAvailRawData;

    // Filter for DUIDs with zero availability at any interval
    if (showOnlyZeroAvail) {
      const dataField =
        selectedVariable === "generation_pasa_availability"
          ? "pasa_avail"
          : "maxavail";
      const duidsWithZero = new Set();
      maxAvailRawData.forEach((row) => {
        if (row[dataField] === 0) {
          duidsWithZero.add(row.duid);
        }
      });
      filteredData = filteredData.filter((row) =>
        duidsWithZero.has(row.duid)
      );
    }

    // Apply region filter
    if (
      selectedRegions.length > 0 &&
      selectedRegions.length < ALL_REGIONS.length
    ) {
      filteredData = filteredData.filter((row) =>
        selectedRegions.includes(row.region_id)
      );
    }

    // Apply fuel type filter
    const currentAvailableFuelTypes = [
      ...new Set(
        maxAvailRawData.map((row) => row.fuel_type).filter(Boolean)
      ),
    ];
    if (
      selectedFuelTypes.length > 0 &&
      selectedFuelTypes.length < currentAvailableFuelTypes.length
    ) {
      filteredData = filteredData.filter((row) =>
        selectedFuelTypes.includes(row.fuel_type)
      );
    }

    // Apply portfolio filter
    const currentAvailablePortfolios = [
      ...new Set(
        maxAvailRawData.map((row) => row.portfolioid).filter(Boolean)
      ),
    ];
    if (
      selectedPortfolios.length > 0 &&
      selectedPortfolios.length < currentAvailablePortfolios.length
    ) {
      filteredData = filteredData.filter((row) =>
        selectedPortfolios.includes(row.portfolioid)
      );
    }

    // Apply station and DUID filters
    if (selectedStations.length > 0) {
      filteredData = filteredData.filter((row) =>
        selectedStations.includes(row.stationid)
      );
    }
    if (selectedDuids.length > 0) {
      filteredData = filteredData.filter((row) =>
        selectedDuids.includes(row.duid)
      );
    }

    const transformedMaxAvail = transformMaxAvailData(
      filteredData,
      aggregationLevel,
      chartLevel,
      [selectedVariable]
    );
    setMaxAvailData(transformedMaxAvail);
  }, [
    maxAvailRawData,
    showOnlyZeroAvail,
    selectedVariable,
    chartLevel,
    selectedRegions,
    selectedFuelTypes,
    selectedPortfolios,
    selectedStations,
    selectedDuids,
    aggregationLevel,
  ]);

  // Effect 3: Auto-refresh interval
  useEffect(() => {
    if (!autoRefresh || !runDateTime) return;

    const intervalId = setInterval(async () => {
      try {
        const response = await api.getMaxRunDateTime();
        if (response.run_datetime !== runDateTime) {
          setRunDateTime(response.run_datetime);
        }
      } catch (error) {
        console.warn("Auto-refresh failed:", error);
      }
    }, 30000);

    return () => clearInterval(intervalId);
  }, [autoRefresh, runDateTime]);

  if (initialLoad && loading) {
    return (
      <div style={{ textAlign: "center", padding: "100px" }}>
        <Spin size="large" tip="Loading MaxAvail data..." />
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? "12px 8px" : "24px" }}>
      <Header
        chartLevel={chartLevel}
        setChartLevel={setChartLevel}
        selectedFuelTypes={selectedFuelTypes}
        setSelectedFuelTypes={setSelectedFuelTypes}
        availableFuelTypes={availableFuelTypes}
        selectedVariable={selectedVariable}
        setSelectedVariable={setSelectedVariable}
        showOnlyZeroAvail={showOnlyZeroAvail}
        setShowOnlyZeroAvail={setShowOnlyZeroAvail}
        viewMode={viewMode}
        setViewMode={setViewMode}
        autoRefresh={autoRefresh}
        setAutoRefresh={setAutoRefresh}
        sharedTooltip={sharedTooltip}
        setSharedTooltip={setSharedTooltip}
        runDateTime={runDateTime}
        onRefresh={handleRefresh}
        loading={loading}
        isMobile={isMobile}
      />

      {isMobile && (
        <FilterSidebar
          runDateTime={runDateTime}
          selectedRegions={ALL_REGIONS}
          initialCheckedKeys={savedSettings.sidebarCheckedKeys}
          onSelectionChange={handleSidebarSelectionChange}
          collapsed={sidebarCollapsed}
          setCollapsed={setSidebarCollapsed}
          isMobile={isMobile}
        >
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            <div>
              <Typography.Text strong>Chart Level:</Typography.Text>
              <Select
                value={chartLevel}
                onChange={setChartLevel}
                style={{ width: "100%", marginTop: 4 }}
                options={[
                  { value: "system", label: "System" },
                  { value: "region", label: "Region" },
                  { value: "fuel_type", label: "Fuel Type" },
                  { value: "portfolio", label: "Portfolio" },
                  { value: "station", label: "Station" },
                  { value: "duid", label: "DUID" },
                ]}
              />
            </div>
            <div>
              <Space size={4}>
                <Typography.Text strong>Select Variable:</Typography.Text>
                <Tooltip title="data from MMS.STPASA_DUIDAVAILABILITY table">
                  <InfoCircleOutlined style={{ color: "#13a89e", cursor: "help" }} />
                </Tooltip>
              </Space>
              <Select
                value={selectedVariable}
                onChange={setSelectedVariable}
                style={{ width: "100%", marginTop: 4 }}
                options={[
                  { value: "generation_max_availability", label: "Generation Max Availability" },
                  { value: "generation_pasa_availability", label: "Generation PASA Availability" },
                ]}
              />
            </div>
            <div>
              <Typography.Text strong>View Mode:</Typography.Text>
              <Select
                value={viewMode}
                onChange={setViewMode}
                style={{ width: "100%", marginTop: 4 }}
                options={[
                  { value: "chart", label: "Charts" },
                  { value: "table", label: "Table" },
                  { value: "both", label: "Both" },
                ]}
              />
            </div>
            {availableFuelTypes && availableFuelTypes.length > 0 && (
              <div>
                <Typography.Text strong>Fuel Types:</Typography.Text>
                <div style={{ marginTop: 4 }}>
                  <Checkbox.Group
                    options={availableFuelTypes.map((ft) => ({
                      label: ft,
                      value: ft,
                    }))}
                    value={selectedFuelTypes}
                    onChange={setSelectedFuelTypes}
                  />
                </div>
              </div>
            )}
            <Space>
              <Typography.Text strong>Show only zero avail:</Typography.Text>
              <Tooltip title="Only DUIDs with at least one zero value are shown">
                <InfoCircleOutlined style={{ color: "#13a89e", cursor: "help" }} />
              </Tooltip>
              <Switch
                checked={showOnlyZeroAvail}
                onChange={setShowOnlyZeroAvail}
              />
            </Space>
            <Space>
              <Typography.Text strong>Show all tooltip values:</Typography.Text>
              <Switch checked={sharedTooltip} onChange={setSharedTooltip} />
            </Space>
            <Space>
              <Typography.Text strong>Auto-refresh (30s):</Typography.Text>
              <Switch checked={autoRefresh} onChange={setAutoRefresh} />
            </Space>
          </Space>
        </FilterSidebar>
      )}

      <Row
        gutter={isMobile || sidebarCollapsed ? 0 : 24}
        style={{ marginTop: 16, marginLeft: 0, marginRight: 0, flexWrap: "nowrap" }}
      >
        {!isMobile && (
          <Col
            xs={24}
            md={sidebarCollapsed ? 1 : 8}
            lg={sidebarCollapsed ? 1 : 6}
            style={{
              position: "relative",
              paddingLeft: 0,
              overflow: "hidden",
              transition: "flex 0.3s ease, max-width 0.3s ease",
              ...(sidebarCollapsed && {
                flex: "0 0 0px",
                maxWidth: "0px",
              }),
            }}
          >
            <FilterSidebar
              runDateTime={runDateTime}
              selectedRegions={ALL_REGIONS}
              initialCheckedKeys={savedSettings.sidebarCheckedKeys}
              onSelectionChange={handleSidebarSelectionChange}
              collapsed={sidebarCollapsed}
              setCollapsed={setSidebarCollapsed}
              isMobile={isMobile}
            />
          </Col>
        )}
        {!isMobile && (
          <div
            style={{
              position: "relative",
              width: 0,
              zIndex: 1001,
            }}
          >
            <Button
              icon={
                sidebarCollapsed ? <RightOutlined /> : <LeftOutlined />
              }
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              style={{
                position: "absolute",
                left: -16,
                top: 0,
                borderRadius: "4px",
                padding: "20px 8px",
                height: "auto",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                backgroundColor: "#13a89e",
                borderColor: "#13a89e",
                color: "white",
              }}
            />
          </div>
        )}

        <Col
          xs={24}
          md={isMobile ? 24 : sidebarCollapsed ? 23 : 16}
          lg={isMobile ? 24 : sidebarCollapsed ? 23 : 18}
          style={{
            paddingLeft: 0,
            transition: "flex 0.3s ease, max-width 0.3s ease",
            ...(sidebarCollapsed && { flex: "1 1 0", maxWidth: "100%" }),
          }}
        >
          {(viewMode === "chart" || viewMode === "both") && (
            <MaxAvailChart
              data={maxAvailData}
              aggregationLevel={aggregationLevel}
              chartLevel={chartLevel}
              loading={loading}
              sharedTooltip={sharedTooltip}
            />
          )}

          {(viewMode === "table" || viewMode === "both") && (
            <div style={{ marginTop: viewMode === "both" ? 24 : 0 }}>
              <DataTable
                data={maxAvailRawData}
                aggregationLevel={aggregationLevel}
                loading={loading}
              />
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default MaxAvailPage;
